// Route -> agent-chat context. Pure: called once when a chat is created (and by
// "New chat"); navigation never re-anchors an existing conversation.

export type ChatPageType = 'campaign' | 'insertion_order' | 'contract' | 'board' | 'generic';

export type ChatEntityKey = 'contract_id' | 'campaign_id' | 'insertion_order_id';

export type ChatContext = {
  pageType: ChatPageType;
  entities: Partial<Record<ChatEntityKey, number>>;
};

type Rule = {
  re: RegExp;
  pageType: ChatPageType;
  // Capture-group index -> entity key.
  keys: ChatEntityKey[];
};

// Ordered: first match wins. Grounded in src/constants/routeUrls.js + the route
// tree in src/App.tsx. Ids are always numeric, which is what keeps `new` and
// `advertisers` segments from being read as ids.
const RULES: Rule[] = [
  { re: /^\/admin\/contracts\/(\d+)\/adgroups\/(\d+)$/, pageType: 'campaign', keys: ['contract_id', 'campaign_id'] },
  { re: /^\/admin\/adgroups\/(\d+)\/products(\/.*)?$/, pageType: 'campaign', keys: ['campaign_id'] },
  {
    re: /^\/admin\/contracts\/(\d+)\/lineitems\/(\d+)$/,
    pageType: 'insertion_order',
    keys: ['contract_id', 'insertion_order_id'],
  },
  {
    re: /^\/admin\/contracts\/(\d+)\/adgroups\/new\/(\d+)$/,
    pageType: 'insertion_order',
    keys: ['contract_id', 'insertion_order_id'],
  },
  { re: /^\/admin\/contracts\/(\d+)(\/(reports|config|lineitems\/new))?$/, pageType: 'contract', keys: ['contract_id'] },
  { re: /^\/admin\/alerts$/, pageType: 'board', keys: [] },
];

const normalize = (pathname: string): string => {
  const cut = (pathname || '/').split(/[?#]/)[0];
  return cut.length > 1 && cut.endsWith('/') ? cut.slice(0, -1) : cut;
};

export const contextFromLocation = (pathname: string): ChatContext => {
  const path = normalize(pathname);
  const rule = RULES.find((r) => r.re.test(path));
  if (!rule) return { pageType: 'generic', entities: {} };

  const match = path.match(rule.re) as RegExpMatchArray;
  const entities: Partial<Record<ChatEntityKey, number>> = {};
  rule.keys.forEach((key, i) => {
    entities[key] = Number(match[i + 1]);
  });
  return { pageType: rule.pageType, entities };
};

// The narrowest pinned entity is what the agent answers about by default, and what
// names a thread ("campaign 433302").
export const contextPhrase = (context: ChatContext): string | null => {
  const entries = Object.entries(context.entities);
  if (!entries.length) return null;
  const [key, value] = entries[entries.length - 1];
  return `${key.replace('_id', '').replace('_', ' ')} ${value}`;
};

// Public routes render outside ProtectedRoute (App.tsx), so the drawer must
// suppress itself on them explicitly.
const PUBLIC_PREFIXES = ['/login', '/sso/auth', '/shared_gallery'];

export const isPublicPath = (pathname: string): boolean => {
  const path = normalize(pathname);
  return PUBLIC_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`));
};
